// Package Imports
import { queryOptions, useMutation } from '@tanstack/react-query'

// Core Imports
import type { TResponseError } from '@/core/types/axios'
import type { TMutation } from '@/core/types/react-query'
import type { TAdminListParams } from '@/core/lib/table-query'

// Module Imports
import {
  createQuestion,
  deleteQuestion,
  editQuestion,
  getQuestionDetail,
  getQuestionList,
  uploadQuestions,
} from '@/modules/question/question.service'
import type {
  TCreateQuestionRequest,
  TEditQuestionRequest,
  TUploadQuestionsRequest,
} from '@/modules/question/question.types'

export const questionListQueryOptions = (params: TAdminListParams) =>
  queryOptions({
    queryKey: ['questionList', params],
    queryFn: () => getQuestionList(params),
  })

export const questionDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['questionDetail', id],
    queryFn: () => getQuestionDetail(id),
    staleTime: 0,
  })

export function useCreateQuestion({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TCreateQuestionRequest>) {
  return useMutation<void, TResponseError, TCreateQuestionRequest>({
    mutationFn: createQuestion,
    onSuccess,
    onError,
  })
}

export function useUploadQuestions({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TUploadQuestionsRequest>) {
  return useMutation<void, TResponseError, TUploadQuestionsRequest>({
    mutationFn: uploadQuestions,
    onSuccess,
    onError,
  })
}

export function useEditQuestion({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, TEditQuestionRequest>) {
  return useMutation<void, TResponseError, TEditQuestionRequest>({
    mutationFn: editQuestion,
    onSuccess,
    onError,
  })
}

export function useDeleteQuestion({
  onSuccess,
  onError,
}: TMutation<void, TResponseError, string>) {
  return useMutation<void, TResponseError, string>({
    mutationFn: deleteQuestion,
    onSuccess,
    onError,
  })
}
